import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import type { ChipState } from "./functionChipState.ts";

export function FunctionChip({
  id,
  icon,
  name,
  suffix,
  value,
  meter,
  state,
  stateTestid,
  alert,
  testid,
  title,
  className,
  onOpen,
}: {
  id: string;
  icon: ReactNode;
  name: string;
  suffix?: ReactNode;
  value: ReactNode;
  meter?: number | null;
  /** Right-aligned process-state pill; null/undefined renders no pill. */
  state?: ChipState | null;
  stateTestid?: string;
  alert?: boolean;
  testid?: string;
  title?: string;
  className?: string;
  onOpen?: (el: HTMLElement) => void;
}) {
  function handleKey(event: KeyboardEvent<HTMLDivElement>) {
    if (!onOpen) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen(event.currentTarget);
    }
  }

  function handleClick(event: MouseEvent<HTMLDivElement>) {
    if (!onOpen) return;
    onOpen(event.currentTarget);
  }

  return (
    <div
      className={`stage${alert ? " alert" : ""}${className ? ` ${className}` : ""}`}
      data-testid={testid ?? `chip-${id}`}
      data-chip={id}
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      title={title}
      onClick={onOpen ? handleClick : undefined}
      onKeyDown={handleKey}
      style={onOpen ? { cursor: "pointer" } : undefined}
    >
      <div className="stage-top">
        {icon}
        <span className="nm">{name}</span>
        {suffix}
      </div>
      <div className="mv">
        {value}
        {meter != null && (
          <span className="mini-meter">
            <i style={{ width: `${Math.max(0, Math.min(100, meter))}%` }} />
          </span>
        )}
        {state && (
          <span className="chip-state" data-testid={stateTestid} title={state.title}>
            <span className={`chip-dot ${state.tone}`} />
            {state.label}
          </span>
        )}
      </div>
    </div>
  );
}
